import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, tap } from 'rxjs';

/**
 * Idempotency Interceptor (Functional)
 * 
 * Prevents duplicate API requests by:
 * 1. Generating unique idempotency keys for POST/PUT/PATCH requests
 * 2. Storing them in sessionStorage based on request fingerprint
 * 3. Attaching the key as 'Idempotency-Key' header
 * 4. Removing the key after successful response
 * 
 * This protects against:
 * - User double-clicks
 * - Network retries
 * - Browser refreshes during request
 * - Multiple rapid clicks on action buttons
 */

const HEADER = 'Idempotency-Key';
const STORAGE_PREFIX = 'idem_';

// Skip idempotency for these endpoints
const SKIP_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/health',
  '/messages/unread' // Reading doesn't need idempotency
];

/**
 * Create a unique fingerprint from request method, URL, and body
 */
function createFingerprint(req: HttpRequest<any>): string {
  const body = req.body ? JSON.stringify(req.body) : '';
  const fingerprint = `${req.method}|${req.urlWithParams}|${body}`;
  
  try {
    return btoa(fingerprint);
  } catch (e) {
    return simpleHash(fingerprint);
  }
}

/**
 * Simple hash function for fingerprint
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Generate a unique key using UUID or fallback
 */
function generateKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Check if this request should skip idempotency
 */
function shouldSkip(url: string): boolean {
  return SKIP_PATHS.some(path => url.includes(path));
}

/**
 * Functional interceptor for idempotency
 */
export const IdempotencyInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Only apply to mutation methods
  if (!['POST', 'PUT', 'PATCH'].includes(req.method)) {
    return next(req);
  }

  // Skip idempotency for certain endpoints
  if (shouldSkip(req.url)) {
    return next(req);
  }

  // Create fingerprint from request details
  const fingerprint = createFingerprint(req);
  const storageKey = STORAGE_PREFIX + fingerprint;

  // Check if we already have a key for this exact request
  let idempotencyKey = sessionStorage.getItem(storageKey);
  
  if (!idempotencyKey) {
    // Generate new key and store it
    idempotencyKey = generateKey();
    sessionStorage.setItem(storageKey, idempotencyKey);
  }

  // Clone request and add idempotency header
  const clonedRequest = req.clone({
    setHeaders: {
      [HEADER]: idempotencyKey
    }
  });

  return next(clonedRequest).pipe(
    tap({
      next: (event) => {
        // Remove key after successful response
        if (event instanceof HttpResponse && event.ok) {
          sessionStorage.removeItem(storageKey);
        }
      },
      error: (error) => {
        // Keep key on error so retry can use same identifier
        console.debug(`[Idempotency] Request failed, key retained for retry: ${idempotencyKey}`);
      }
    })
  );
};
